import React, { useEffect, useState, useRef } from 'react';
import { MessageSquare, Send, X, Minimize2, Maximize2, Sparkles } from 'lucide-react';
interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}
interface AIChatAssistantProps {
  context?: 'reports' | 'dashboard' | 'settings';
}
export function AIChatAssistant({
  context = 'reports'
}: AIChatAssistantProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState<Message[]>([{
    id: '1',
    role: 'assistant',
    content: `Hi! I'm your surgical reconciliation assistant. I can help you analyze reports, find patterns, and answer questions about your cases. What would you like to know?`,
    timestamp: new Date()
  }]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  const suggestedQuestions = {
    reports: ['Show me cases with overrides this week', 'What are the most common missing instruments?', 'Compare completion rates by OR room', 'Show trends in reconciliation time'],
    dashboard: ['What should I check before scanning?', 'How do I handle a missing instrument?', 'Explain the confidence metrics', 'What are best practices for reconciliation?'],
    settings: ['How do I calibrate the camera?', 'What detection sensitivity should I use?', 'How do I set up alerts?', 'Explain the security settings']
  };
  const handleSend = async () => {
    if (!input.trim()) return;
    const userMessage: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: input,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
    // Simulate AI response
    setTimeout(() => {
      const response = generateResponse(input, context);
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        role: 'assistant',
        content: response,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
      setIsTyping(false);
    }, 1000);
  };
  const generateResponse = (query: string, ctx: string): string => {
    const lowerQuery = query.toLowerCase();
    // Reports context responses
    if (ctx === 'reports') {
      if (lowerQuery.includes('override')) {
        return 'In the past week, there were 8 cases with manual overrides:\n\n• 3 cases: Tool damaged/removed\n• 2 cases: Discarded in waste\n• 2 cases: Confirmed off-tray\n• 1 case: Detection error\n\nOR Room 3 has the highest override rate (12%). Would you like me to generate a detailed report?';
      }
      if (lowerQuery.includes('missing') || lowerQuery.includes('common')) {
        return 'Top 3 most frequently missing instruments:\n\n1. Scalpel Handle #3 (14 occurrences)\n2. Mosquito Hemostat (9 occurrences)\n3. Towel Clip (7 occurrences)\n\nRecommendation: Consider adding RFID tags to these high-risk items.';
      }
      if (lowerQuery.includes('completion') || lowerQuery.includes('rate')) {
        return 'Completion rates by OR room (last 30 days):\n\n• OR Room 1: 99.2%\n• OR Room 2: 98.7%\n• OR Room 3: 96.5%\n• OR Room 4: 99.8% ⭐\n\nOverall average: 98.6%. OR Room 4 has the best performance!';
      }
      if (lowerQuery.includes('trend') || lowerQuery.includes('time')) {
        return 'Reconciliation time trends:\n\n• Average: 52 minutes\n• Fastest: 28 minutes (OR Room 4)\n• Slowest: 78 minutes (OR Room 3)\n\nTip: Cases with pre-op baseline scans complete 35% faster.';
      }
    }
    // Dashboard context responses
    if (ctx === 'dashboard') {
      if (lowerQuery.includes('before scan') || lowerQuery.includes('check')) {
        return 'Pre-scan checklist:\n\n✓ Ensure tray is fully visible to camera\n✓ Remove any covering or drapes\n✓ Check camera "LIVE" indicator is on\n✓ Verify correct case ID is displayed\n✓ Confirm all instruments are in their slots\n\nReady to scan? Click "Scan Tray" when ready.';
      }
      if (lowerQuery.includes('missing')) {
        return 'When an instrument is missing:\n\n1. First, click "Re-scan Tray" - often resolves false positives\n2. If still missing, physically check:\n   • Sterile field\n   • Drape pockets\n   • Mayo stand\n   • Kick bucket\n3. If confirmed missing, use "Resolve Manually"\n4. Document reason and get witness verification\n\nNever finalize with unresolved discrepancies.';
      }
      if (lowerQuery.includes('confidence') || lowerQuery.includes('metric')) {
        return 'Confidence metrics explained:\n\n• Detection Confidence: AI certainty (aim for >95%)\n• Items Detected: Count of instruments found\n• Processing Time: Scan speed (<60ms is good)\n• Last Scan: Timestamp for audit trail\n\nLow confidence? Try re-scanning with better lighting.';
      }
    }
    // Settings context responses
    if (ctx === 'settings') {
      if (lowerQuery.includes('calibrate')) {
        return 'Camera calibration steps:\n\n1. Go to Settings → Camera & Detection\n2. Click "Calibrate Camera"\n3. Place calibration card on tray\n4. Follow on-screen alignment guide\n5. System will auto-adjust focus and exposure\n\nCalibrate monthly or after camera repositioning.';
      }
      if (lowerQuery.includes('sensitivity')) {
        return 'Detection sensitivity guide:\n\n• 70-80%: Permissive (fewer false positives)\n• 85-90%: Balanced (recommended)\n• 95-100%: Strict (catches everything)\n\nCurrent setting: 85%. Adjust based on your false positive rate.';
      }
    }
    // Default response
    return `I understand you're asking about "${query}". Could you provide more details? I can help with:\n\n• Analyzing case reports and trends\n• Explaining reconciliation workflows\n• Troubleshooting detection issues\n• Best practices and recommendations\n\nWhat specific information do you need?`;
  };
  const handleSuggestionClick = (question: string) => {
    setInput(question);
  };
  if (!isOpen) {
    return <button onClick={() => setIsOpen(true)} className="fixed bottom-6 right-6 w-14 h-14 bg-info hover:bg-blue-500 text-white rounded-full shadow-lg flex items-center justify-center transition-all hover:scale-110 z-40" data-id="element-17">
        <Sparkles className="w-6 h-6" data-id="element-18" />
      </button>;
  }
  return <div className={`fixed bottom-6 right-6 bg-surface border border-border rounded-modal shadow-2xl z-40 transition-all ${isMinimized ? 'w-80 h-16' : 'w-96 h-[600px]'}`} data-id="element-19">
      {/* Header */}
      <div className="bg-surface-2 border-b border-border p-4 flex items-center justify-between rounded-t-modal" data-id="element-20">
        <div className="flex items-center gap-3" data-id="element-21">
          <div className="w-8 h-8 bg-info rounded-full flex items-center justify-center" data-id="element-22">
            <Sparkles className="w-5 h-5 text-white" data-id="element-23" />
          </div>
          <div data-id="element-24">
            <h3 className="font-bold text-text-primary" data-id="element-25">AI Assistant</h3>
            <p className="text-xs text-text-secondary" data-id="element-26">
              Powered by surgical intelligence
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2" data-id="element-27">
          <button onClick={() => setIsMinimized(!isMinimized)} className="p-1.5 hover:bg-surface rounded-input transition-colors" data-id="element-28">
            {isMinimized ? <Maximize2 className="w-4 h-4 text-text-secondary" data-id="element-29" /> : <Minimize2 className="w-4 h-4 text-text-secondary" data-id="element-30" />}
          </button>
          <button onClick={() => setIsOpen(false)} className="p-1.5 hover:bg-surface rounded-input transition-colors" data-id="element-31">
            <X className="w-4 h-4 text-text-secondary" data-id="element-32" />
          </button>
        </div>
      </div>

      {!isMinimized && <>
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 h-[440px]" data-id="element-33">
            {messages.map(message => <div key={message.id} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-34">
                <div className={`max-w-[80%] rounded-lg p-3 ${message.role === 'user' ? 'bg-info text-white' : 'bg-surface-2 text-text-primary border border-border'}`} data-id="element-35">
                  <p className="text-sm whitespace-pre-line" data-id="element-36">
                    {message.content}
                  </p>
                  <span className="text-xs opacity-70 mt-1 block" data-id="element-37">
                    {message.timestamp.toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit'
              })}
                  </span>
                </div>
              </div>)}

            {isTyping && <div className="flex justify-start" data-id="element-38">
                <div className="bg-surface-2 border border-border rounded-lg p-3" data-id="element-39">
                  <div className="flex gap-1" data-id="element-40">
                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" data-id="element-41" />
                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{
                animationDelay: '0.1s'
              }} data-id="element-42" />
                    <div className="w-2 h-2 bg-text-secondary rounded-full animate-bounce" style={{
                animationDelay: '0.2s'
              }} data-id="element-43" />
                  </div>
                </div>
              </div>}

            <div ref={messagesEndRef} data-id="element-44" />
          </div>

          {/* Suggested Questions */}
          {messages.length <= 1 && <div className="px-4 pb-2" data-id="element-45">
              <p className="text-xs text-text-secondary mb-2 font-bold uppercase" data-id="element-46">
                Suggested questions:
              </p>
              <div className="space-y-1" data-id="element-47">
                {suggestedQuestions[context].slice(0, 2).map((question, idx) => <button key={idx} onClick={() => handleSuggestionClick(question)} className="w-full text-left text-xs p-2 bg-surface-2 hover:bg-surface border border-border rounded-input text-text-secondary hover:text-text-primary transition-colors" data-id="element-48">
                      {question}
                    </button>)}
              </div>
            </div>}

          {/* Input */}
          <div className="border-t border-border p-4" data-id="element-49">
            <div className="flex gap-2" data-id="element-50">
              <input type="text" value={input} onChange={e => setInput(e.target.value)} onKeyPress={e => e.key === 'Enter' && handleSend()} placeholder="Ask me anything..." className="flex-1 bg-surface-2 border border-border rounded-input px-3 py-2 text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:ring-2 focus:ring-info" data-id="element-51" />
              <button onClick={handleSend} disabled={!input.trim()} className={`p-2 rounded-input transition-colors ${input.trim() ? 'bg-info hover:bg-blue-500 text-white' : 'bg-surface-2 text-text-secondary cursor-not-allowed'}`} data-id="element-52">
                <Send className="w-5 h-5" data-id="element-53" />
              </button>
            </div>
          </div>
        </>}
    </div>;
}